/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-29 10:33:30
 *
 *******************************************************/
// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import { DataTable, Thumbnail, Button, Stack, SkeletonDisplayText } from '@shopify/polaris';
import { EditMajor, DeleteMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import GroupProduct from './group-product';
import ConfirmDeleteModal from 'src/components/views/recommendation/confirm-delete-modal';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import { pickImage } from 'src/helpers/image-helper';
import { LIMIT_SHOW_RECOMMENDATION_PRODUCT } from 'src/constants/recommendation';
import NoRecommendation from '../no-recommendation';
import type { ManualRecommendationResponseType } from 'src/models/recommendation';

type State = {
  isShowDeleteModal: boolean
};

type Props = {
  loading: boolean,
  loadedSuccess: boolean,
  manualRecommendations: ManualRecommendationResponseType[],
  manualDeleting: boolean,
  manualDeleteSuccess: boolean,
  onDelete: (recommendationId: string) => void
};

type ComposedProps = Props & WithI18nProps;
class HandPickedProductType extends Component<ComposedProps, State> {
  willDeleteId = '';
  state = {
    isShowDeleteModal: false
  };

  componentWillReceiveProps(nextProps) {
    const { manualDeleteSuccess } = this.props;
    const { manualDeleteSuccess: nextManualDeleteSuccess } = nextProps;
    if (!manualDeleteSuccess && nextManualDeleteSuccess) {
      this.handleToggleModalDelete();
    }
  }

  render() {
    const { i18n, loading, loadedSuccess, manualRecommendations, manualDeleting } = this.props;
    const { isShowDeleteModal } = this.state;
    const rowsRecommendation = this.buildRows();

    return (
      <div className="BC-Page__ManualType__HandPickedProduct">
        {_.isEmpty(manualRecommendations) && !loading && loadedSuccess ? (
          <Stack vertical alignment="center" distribution="center">
            <NoRecommendation />
          </Stack>
        ) : (
          <DataTable
            verticalAlign="middle"
            columnContentTypes={['text', 'text', 'text', 'text']}
            headings={[
              '',
              <p className="BC-Page__ManualType__HandPickedProduct__ColumnTitle">
                {i18n.translate(
                  'AdminHTML.Recommendation.ManualRecommendation.HandPickedProduct.Column1_Title'
                )}
              </p>,
              <p className="BC-Page__ManualType__HandPickedProduct__ColumnTitle">
                {i18n.translate(
                  'AdminHTML.Recommendation.ManualRecommendation.HandPickedProduct.Column2_Title'
                )}
              </p>,
              <p className="BC-Page__ManualType__HandPickedProduct__ColumnTitle">
                {i18n.translate(
                  'AdminHTML.Recommendation.ManualRecommendation.HandPickedProduct.Column3_Title'
                )}
              </p>
            ]}
            rows={rowsRecommendation}
          />
        )}

        <ConfirmDeleteModal
          active={isShowDeleteModal}
          deleting={manualDeleting}
          title={i18n.translate('AdminHTML.Recommendation.ManualRecommendation.ModalDelete.title')}
          content={i18n.translate(
            'AdminHTML.Recommendation.ManualRecommendation.ModalDelete.content'
          )}
          onToggleModal={this.handleToggleModalDelete}
          onDelete={this.handleDelete}
        />
      </div>
    );
  }

  buildRows() {
    const { loading, manualRecommendations } = this.props;
    let rows = [];
    if (loading) {
      for (let i = 0; i < 5; i++) {
        rows.push([
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />
        ]);
      }
    } else {
      (manualRecommendations || []).forEach(item => {
        const { mainProduct = {}, recommendedProducts } = item;
        if (_.isEmpty(mainProduct)) {
          return;
        }

        const { images: imageMainProducts } = mainProduct;
        let imageMainProduct = pickImage(imageMainProducts);

        const recommendProductImages = recommendedProducts.map(product => {
          const { images: productImage } = product;
          return pickImage(productImage);
        });
        rows.push([
          <Thumbnail source={imageMainProduct} size="small" />,
          <Stack alignment="center">
            <p className="BC-Page__ManualType__HandPickedProduct__SourceProduct">
              {mainProduct?.title}
            </p>
          </Stack>,
          !_.isEmpty(recommendProductImages) ? (
            <GroupProduct
              limitShow={LIMIT_SHOW_RECOMMENDATION_PRODUCT}
              thumbnails={recommendProductImages}
            />
          ) : null,
          this.renderRecommendationAction(item.id)
        ]);
      });
    }
    return rows;
  }

  renderRecommendationAction(id) {
    return (
      <div className="BC-Page__ManualType__HandPickedProduct__ButtonGroups">
        <Button size="slim" icon={EditMajor} plain onClick={() => this.handleToEdit(id)} />
        <Button
          size="slim"
          icon={DeleteMajor}
          plain
          onClick={() => this.handleToggleModalDelete(id)}
        />
      </div>
    );
  }

  handleToggleModalDelete = id => {
    if (this.state.isShowDeleteModal) {
      this.willDeleteId = '';
    } else {
      this.willDeleteId = id;
    }
    this.setState({
      isShowDeleteModal: !this.state.isShowDeleteModal
    });
  };

  handleDelete = () => {
    const { onDelete } = this.props;
    onDelete(this.willDeleteId);
    this.willDeleteId = '';
  };

  handleToEdit = id => {
    history.push(routes.manualRecommendation.view(id));
  };
}

export default withI18n()(HandPickedProductType);
